import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDeviceQuery } from "@bokio/elements/DeviceQuery/useDeviceQuery";
import { useResizeObserver } from "@bokio/hooks/useResizeObserver/useResizeObserver";
import { noop } from "@bokio/shared/utils";

import { bestMatch, boxToPolygonString, getMatches } from "./ocrRenderer.utils";

import type { OcrMatchName, SelectedPredictions, ZoomState } from "./ocrRenderer.utils";
import type * as m from "@bokio/mobile-web-shared/core/model/model";
import type { FC, PropsWithChildren } from "react";

import * as styles from "./ocrRenderer.scss";

type FieldsPrediction = m.Classes.AI.ReceiptFieldsPrediction;
interface OcrRendererContextType {
	addHideType: (type: OcrMatchName) => void;
	setImageRef: (img: HTMLImageElement | null) => void;
	setPredictions: (predictions?: FieldsPrediction) => void;
	setOffset: (offset: number) => void;
	rerenderOcr: () => void;
	setHideAll: (value: boolean) => void;
	setHideAllLines: (value: boolean) => void;
	setShowType: (type: OcrMatchName | undefined) => void;
	selectedPredictions: SelectedPredictions;
	setSelectedPredictions: (value: SelectedPredictions) => void;
	setZoomState: (position: ZoomState) => void;
	paymentInformation: m.Classes.AI.PaymentDetailsMatch | undefined;
}

const defaultSelectedPredictions = {
	date: undefined,
	paymentDate: undefined,
	paymentReference: undefined,
	amount: undefined,
};

export const OcrRendererContext = createContext<OcrRendererContextType>({
	addHideType: noop,
	setImageRef: noop,
	setPredictions: noop,
	setOffset: noop,
	rerenderOcr: noop,
	setHideAll: noop,
	setHideAllLines: noop,
	setShowType: noop,
	selectedPredictions: defaultSelectedPredictions,
	setSelectedPredictions: noop,
	setZoomState: noop,
	paymentInformation: undefined,
});

interface OcrRendererProps {
	hide?: boolean;
	multiPrediction?: boolean;
	defaultToBestMatch?: boolean;
}

export const OcrRenderer: FC<PropsWithChildren<OcrRendererProps>> = props => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [zoomState, setZoomState] = useState({ positionX: 0, positionY: 0, scale: 1 });
	const [paymentInformation, setPaymentInformation] = useState<m.Classes.AI.PaymentDetailsMatch | undefined>(undefined);
	const [hideAll, setHideAll] = useState(false);
	const [, setCount] = useState(0);
	const [offsetLeft, setOffsetLeft] = useState(0);
	const [hideTypes, setHideTypes] = useState<OcrMatchName[]>([]);
	const [showType, setShowType] = useState<OcrMatchName | undefined>(undefined);
	const [selectedPredictions, setSelectedPredictions] = useState<SelectedPredictions>(defaultSelectedPredictions);
	const [fieldsPrediction, setFieldPredictions] = useState<FieldsPrediction | undefined>(undefined);
	const { isDesktop } = useDeviceQuery({ minDesktopWidth: 1024 });

	const imageRef = useRef<HTMLImageElement | null>(null);

	const [width, setWidth] = useState<number | undefined>(undefined);
	const onResize = useCallback((rect: DOMRect) => {
		setWidth(rect.width);
	}, []);
	useResizeObserver({ ref: imageRef, onResize });

	useEffect(() => {
		let paymentDate: m.Day | undefined = undefined;
		let sum: number | undefined = undefined;
		const date = bestMatch(fieldsPrediction?.Dates)?.Value;
		//ME: Find any payment reference that we deem fully trusted. Otherwise don't set it
		const paymentReference = bestMatch(fieldsPrediction?.PaymentReferences?.filter(ref => ref.Score === 100))?.Value;
		if (fieldsPrediction?.UserEnteredPaymentDate) {
			paymentDate = fieldsPrediction.UserEnteredPaymentDate;
		} else if (props.defaultToBestMatch) {
			paymentDate = bestMatch(fieldsPrediction?.PaymentDates)?.Value;
		}
		if (fieldsPrediction?.UserEnteredSum) {
			sum = fieldsPrediction.UserEnteredSum;
		} else if (props.defaultToBestMatch) {
			sum = bestMatch(fieldsPrediction?.TotalSums)?.Value;
		}
		setSelectedPredictions(s => {
			const result = { ...s };
			if (sum) {
				result.amount = { value: sum.toString(), index: 0, isDefault: !fieldsPrediction?.UserEnteredSum };
			}
			if (paymentDate) {
				result.paymentDate = {
					value: paymentDate.toISOString(),
					index: 0,
					isDefault: !fieldsPrediction?.UserEnteredPaymentDate,
				};
			}
			if (date) {
				result.date = { value: date.toISOString(), index: 0, isDefault: true };
			}
			if (paymentReference) {
				result.paymentReference = { value: paymentReference, index: 0, isDefault: true };
			}
			return result;
		});

		setPaymentInformation(fieldsPrediction?.PaymentInformation ?? undefined);
	}, [
		fieldsPrediction?.PaymentDates,
		fieldsPrediction?.TotalSums,
		fieldsPrediction?.UserEnteredPaymentDate,
		fieldsPrediction?.UserEnteredSum,
		fieldsPrediction?.Dates,
		fieldsPrediction?.PaymentReferences,
		fieldsPrediction?.PaymentInformation,
		props.defaultToBestMatch,
	]);

	const scale = imageRef.current && width ? width / imageRef.current.naturalWidth : 1;
	const setImageRef = useCallback((img: HTMLImageElement | null) => {
		imageRef.current = img;
	}, []);

	const setInputOutlineVisibility = useCallback(
		(id: OcrMatchName, shouldBeVisible: boolean) => {
			if (!props.multiPrediction) {
				const input = document.querySelector<HTMLInputElement>(`#key_${id}`);
				if (shouldBeVisible) {
					input?.classList.remove("hide-ocr-border");
				} else {
					input?.classList.add("hide-ocr-border");
				}
			}
		},
		[props.multiPrediction],
	);

	const inputFieldGetter = useCallback((field: OcrMatchName) => {
		const inputs = document.querySelectorAll(`#key_${field}`);

		if (field === "amount" && inputs.length > 1) {
			return undefined;
		}
		return inputs.length ? (inputs[0] as HTMLInputElement) : undefined;
	}, []);

	const renderOcr = (image: HTMLImageElement, container: HTMLDivElement) => {
		if (!fieldsPrediction) {
			return null;
		}

		const imageRect = image.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();

		const matches = getMatches({
			multiPrediction: !!props.multiPrediction,
			scale,
			zoomState,
			offsetLeft,
			prediction: fieldsPrediction,
			selectedPredictions,
			image,
			hideTypes,
			showType,
			inputGetter: inputFieldGetter,
		});

		// This approach to set the visibility in render should be done in another way
		matches.hideFields.forEach(f => setInputOutlineVisibility(f, false));

		const leftPad = offsetLeft + 1; // pad + border

		const svgLeft = `${imageRect.left - containerRect.left + container.scrollLeft - offsetLeft - zoomState.positionX}px`;
		const svgTop = `${imageRect.top - containerRect.top + container.scrollTop - zoomState.positionY}px`;

		const availableHeight = window.innerHeight - imageRect.top;

		if (availableHeight < 0) {
			return null;
		}

		return (
			<svg
				style={{
					overflow: "hidden",
					pointerEvents: "none",
					position: "relative",
					left: svgLeft,
					top: svgTop,
					zIndex: 2,
					width: image.width + leftPad,
					height: Math.min(image.height, availableHeight),
				}}
			>
				{matches.fieldMatches.map((m, i) => (
					<g
						key={i}
						style={{ cursor: "pointer", pointerEvents: "auto" }}
						onMouseDown={e => {
							if (showType) {
								const s = { ...selectedPredictions };
								s[showType] = { index: i, value: m.value, isDefault: false };
								setSelectedPredictions(s);
								e.preventDefault();
							}
						}}
						data-testid={`OcrPrediction_${m.name}_${i}`}
					>
						<polygon
							points={boxToPolygonString(m.box)}
							strokeWidth="3"
							strokeOpacity={m.box.color.strokeOpacity}
							fill={m.box.color.fill}
							fillOpacity={m.box.color.fillOpacity}
							stroke={m.box.color.stroke}
						/>
						{m.line && (
							<path
								className={styles.line}
								fill="none"
								strokeWidth="3"
								strokeOpacity="0.7"
								stroke={m.line.stroke}
								d={m.line.d}
							/>
						)}
					</g>
				))}
			</svg>
		);
	};

	const handleHideAll = useCallback(
		(shouldHideAll: boolean) => {
			const ids: OcrMatchName[] = ["amount", "date", "paymentDate", "paymentReference"];
			ids.forEach(id => setInputOutlineVisibility(id, !shouldHideAll));
			setHideAll(shouldHideAll);
		},
		[setInputOutlineVisibility],
	);

	const handleHideAllLines = useCallback((shouldHideAllLines: boolean) => {
		setHideAll(shouldHideAllLines);
	}, []);

	const setPredictions = useCallback(
		(fieldsPrediction?: FieldsPrediction) => {
			setFieldPredictions(fieldsPrediction);
			handleHideAll(fieldsPrediction === undefined);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleHideAll, selectedPredictions],
	);

	const addHideType = useCallback(
		(type: OcrMatchName) => {
			setHideTypes(prev => [...prev, type]);
			setInputOutlineVisibility(type, false);
		},
		[setInputOutlineVisibility],
	);

	const updateOffset = useCallback((offset: number) => {
		setOffsetLeft(offset);
	}, []);

	// We memoize this so that we don't unnessesarily rerender children unless the state here actually changes
	// If you were to just pass the object directly, it would be considered changed each time even if the
	// values are the same
	const value: OcrRendererContextType = useMemo(
		() => ({
			addHideType,
			setPredictions,
			setImageRef,
			setOffset: updateOffset,
			rerenderOcr: () => setCount(prev => prev + 1),
			setHideAll: handleHideAll,
			setHideAllLines: handleHideAllLines,
			setShowType,
			selectedPredictions: selectedPredictions,
			setSelectedPredictions,
			setZoomState,
			paymentInformation,
		}),
		[
			addHideType,
			setPredictions,
			setImageRef,
			updateOffset,
			handleHideAll,
			handleHideAllLines,
			selectedPredictions,
			setSelectedPredictions,
			paymentInformation,
		],
	);
	return (
		<div className={styles.wrapper} ref={containerRef}>
			<OcrRendererContext value={value}>
				{fieldsPrediction &&
					!props.hide &&
					!hideAll &&
					imageRef.current &&
					containerRef.current &&
					scale &&
					isDesktop && <div className={styles.ocrOverlay}>{renderOcr(imageRef.current, containerRef.current)}</div>}
				{props.children}
			</OcrRendererContext>
		</div>
	);
};
